import { environment } from 'src/environments/environment';
import {
  PriceRequest,
  CustomerResponse,
  PriceResponse,
  SetupIntentResponse,
  SubscriptionResponse,
  DonationMetadata,
  InvoiceResponse,
} from './stripe-models';

/**
 * DonationType is the single source of truth for the donation flow.
 * Its enum values are also the public URL segments (`one-time`, `recurring`),
 * so route generation and persistence can rely on the same literals.
 */
export enum DonationType {
  OneTime = 'one-time',
  Recurring = 'recurring',
}

export function isDonationType(value: unknown): value is DonationType {
  return value === DonationType.OneTime || value === DonationType.Recurring;
}

export type FlowMode = 'single' | 'multiple' | 'popup';
export type FeeCalculationStatus = 'not-calculated' | 'loading' | 'calculated' | 'error';


//viewModel intefaces
export interface CheckoutSummaryVM {
  /** Monto en unidades mayores (25.00) */
  donationAmount: number;
  /** Fees en unidades mayores – undefined hasta que el cálculo esté disponible */
  donationFees?: number;
  /** 'USD', 'MXN', etc. */
  currency: string;
  /** Tipo de donación para el sufijo /mo */
  donationType: DonationType | null;
  /** Si el usuario cubre fees */
  feesCovered: boolean;
  /** true cuando el cálculo de fees es válido y fue obtenido de la API */
  hasCalculatedFees: boolean;
  /** true mientras la llamada a la API está en curso */
  isCalculatingFees: boolean;
}

export interface DonationAmountsVM {
  /** Monto base en centavos (ej. 1500 = $15.00) */
  baseMinor: number;

  /** Fee calculado en centavos (ej. 45 = $0.45) */
  feeMinor: number;

  /** Total que paga el donante (con o sin fees), en centavos */
  totalPaidMinor: number;

  /** Total neto recibido por la organización, en centavos */
  netReceivedMinor: number;

  currency: string;

  /** Tipo de donación (one-time | recurring) */
  donationType: DonationType;

  /** Indica si el donante optó por cubrir los fees */
  feesCovered: boolean;
}

export function calculateFeeMinor(
  baseAmountMinor: number,
  cardFeeType: CardFeeType,
  donationType: DonationType
): number {
  if (!Number.isInteger(baseAmountMinor) || baseAmountMinor <= 0) {
    return 0;
  }

  const cardFee = environment.cardFees[cardFeeType];

  const productPercent =
    donationType === DonationType.Recurring
      ? environment.stripeProductFees.recurringBillingPercent
      : environment.stripeProductFees.oneTimeInvoicePercent;

  const totalPercent =
    (cardFee.percent + productPercent) / 100;

  /*
   * True mathematical markup formula:
   * netAmount = grossAmount - percentageFee - fixedFee
   *
   * grossAmount =
   * (netAmount + fixedFee) / (1 - totalPercentage)
   */
  const grossAmountMinor = Math.ceil(
    (baseAmountMinor + cardFee.fixedMinor) /
      (1 - totalPercent)
  );

  return grossAmountMinor - baseAmountMinor;
}

/**
 * El checkbox SOLO decide si el fee se suma al total.
 * Nunca muta el monto base ni recalcula el fee.
 */
export function applyFeeToTotal(
  baseAmountMinor: number,
  feeMinor: number,
  coverFees: boolean
): number {
  return coverFees ? baseAmountMinor + feeMinor : baseAmountMinor;
}

export function buildDonationAmountsVM(
  baseMinor: number,
  currency: string,
  donationType: DonationType,
  feesCovered: boolean,
  feeType: CardFeeType
): DonationAmountsVM {
  const feeMinor = calculateFeeMinor(baseMinor, feeType, donationType);
  const totalPaidMinor = applyFeeToTotal(baseMinor, feeMinor, feesCovered);
  const netReceivedMinor = feesCovered
    ? baseMinor
    : Math.max(0, baseMinor - feeMinor);

  return {
    baseMinor,
    feeMinor,
    totalPaidMinor,
    netReceivedMinor,
    currency,
    donationType,
    feesCovered,
  };
}

export interface DonorFormValue {
  email: string;
  firstName: string;
  lastName: string;
  displayName: boolean;
  phone: string;
  phoneCode: string;
  isCompany: boolean;
  companyName: string;
  line1: string;
  line2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

/** Draft para Price (Create) usado en Step 2 de ambos flujos */
export type PriceDraft = Omit<PriceRequest, 'action' | 'id'> & {
  action: 'Create';
  productFeeId?: string;
};

export enum CardFeeType {
  Us = 'US',
  International = 'INTERNATIONAL',
  Amex = 'AMEX',
}

export interface FeeRate {
  percent: number;
  fixedMinor: number;
}

export interface FeeReview {
  visible: boolean;
  feeType?: CardFeeType;
  currency?: string;
  baseMinor?: number;
  oldFeeMinor?: number;
  newFeeMinor?: number;
}

export interface CardFeeInformation {
  brand: string;
  country: string;
  feeType: CardFeeType;
}

export interface DonationFlowState {
  isLoading: boolean;
  step: number;
  flowMode: FlowMode | null;
  donationType: DonationType | null;

  priceDraft: PriceDraft | null;

  /** Guardamos lo necesario del precio (sin object/active/unit_amount_decimal) */
  price: Omit<
    PriceResponse,
    'object' | 'active' | 'unit_amount_decimal'
  > | null;

  /** Guardamos lo necesario del customer (sin object/description/created) */
  customer: Omit<CustomerResponse, 'object' | 'description' | 'created'> | null;

  /** Guardamos lo necesario del PI (sin object/customer/metadata) */
  // paymentIntent: Omit<
  //   PaymentIntentResponse,
  //   'object' | 'customer' | 'metadata'
  // > | null;
  invoice: InvoiceResponse | null;

  metadata: DonationMetadata | null;

  feesCovered: boolean;
  cardFeeType: CardFeeType | null;
  /** Estado del cálculo de fees: distingue not-calculated / loading / calculated / error */
  feeCalculationStatus: FeeCalculationStatus;

  isFirstChargeDeferred: boolean;

  firstPaymentDate: string | null;

  feeReview: FeeReview | null;

  /** Guardamos lo necesario del SI (sin object/customer) */
  setupIntent: Omit<SetupIntentResponse, 'object' | 'customer'> | null;

  paymentMethodId: string | null;

  /** Guardamos lo necesario de la subscription (sin object/customer/items/cancel_at_period_end) */
  subscription: Omit<
    SubscriptionResponse,
    'object' | 'customer' | 'items' | 'cancel_at_period_end'
  > | null;

  error: unknown | null;
}
